import { CalendarCheck, Download, Eye, MousePointerClick, PlayCircle, Sparkles, Target, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { quizAnswerLabels } from "../quiz-labels.js";
import { levelContent, quizQuestions } from "../public/config/quizConfig.js";

/** Leads, how many are qualified (≥ USD 100k) and how many clicked their result's button (agendar or curso). */
interface LeadMetrics { leads: number; qualified: number; ctaClicks: number }
interface Summary {
  sessions: number; started: number; reachedForm: number; leads: number; qualified: number;
  resultViewsCalendar: number; resultViewsCourse: number; bookingClicks: number; courseClicks: number;
}
interface Report {
  timeZone: string;
  range: { from: string; to: string };
  dataSince: string | null;
  summary: { current: Summary; previous: Summary };
  daily: Array<{ day: string; sessions: number; leads: number; qualified: number }>;
  funnel: Array<{ step: number; questionId: string; sessions: number; exits: number; medianSeconds: number | null; changedAnswers: number }>;
  funnelResumed: number;
  breakdowns: Record<"channel" | "campaign" | "ad" | "device" | "country", Array<LeadMetrics & { key: string; sessions: number | null }>>;
  profile: Record<"level" | "annualRevenue" | "teamSize", Array<LeadMetrics & { key: string }>>;
  answers: Array<{ questionId: string; answerId: string; leads: number; qualified: number }>;
  formErrors: Array<{ field: string; reason: string; count: number }>;
  submitErrors: Array<{ reason: string; count: number }>;
  resultPage: Array<{ event: string; detail: string; sessions: number }>;
  recentLeads: Array<{
    acceptedAt: string; level: string; score: number; annualRevenue: string | null; teamSize: string | null;
    qualified: boolean; channel: string; campaign: string | null; ad: string | null; device: string | null;
    country: string | null; completionSeconds: number | null; clicked: boolean;
  }>;
}

const answerLabels = quizAnswerLabels as Record<string, Record<string, string>>;
const questionPrompt = (id: string) => id === "contact" ? "Formulario de contacto" : quizQuestions.find((question) => question.id === id)?.prompt ?? id;
const answerLabel = (question: string, answer: string) => answerLabels[question]?.[answer] ?? answer;
const levelLabel = (level: string) => levelContent[level as keyof typeof levelContent]?.title ?? level;
const fieldLabels: Record<string, string> = { fullName: "Nombre", email: "Correo", phone: "Teléfono", consent: "Consentimiento", turnstile: "Verificación" };
const reasonLabels: Record<string, string> = {
  vacio: "Vacío", nombre_invalido: "Nombre inválido (correo, enlace o números)", falta_apellido: "Falta el apellido",
  formato: "Formato inválido", posible_error_tipeo: "Posible error de dedo", faltan_digitos: "Faltan dígitos",
  sobran_digitos: "Sobran dígitos", codigo_pais: "Código de país", no_valido_en_pais: "No válido en el país",
  required: "Obligatorio", invalid: "Inválido", missing_answers: "Faltan respuestas", save_failed: "No se pudo guardar"
};
const number = new Intl.NumberFormat("es-MX");
const pct = (part: number, whole: number) => whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : "—";
const isoDay = (date: Date) => date.toISOString().slice(0, 10);

/** Today in Mexico City (the report's time zone), as YYYY-MM-DD. */
export function todayInMexico(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City" }).format(new Date());
}
export function daysBefore(day: string, count: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - count);
  return isoDay(date);
}

export function QuizAnalytics({ onUnauthorized }: { onUnauthorized: () => void }) {
  const today = todayInMexico();
  const [range, setRange] = useState({ from: daysBefore(today, 29), to: today });
  const [refreshNonce, setRefreshNonce] = useState(0);
  // The last answer and the request it belongs to: "loading" is simply "the answer is for another request".
  const [result, setResult] = useState<{ key: string; report: Report | null; error: string }>({ key: "", report: null, error: "" });
  const requestKey = `${range.from}|${range.to}|${refreshNonce}`;
  const loading = result.key !== requestKey;
  const report = result.report;
  const error = loading ? "" : result.error;

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/v1/admin/analytics?from=${range.from}&to=${range.to}`)
      .then(async (response) => {
        if (response.status === 401) { onUnauthorized(); return; }
        if (!response.ok) throw new Error(String(response.status));
        const next = await response.json() as Report;
        if (!cancelled) setResult({ key: requestKey, report: next, error: "" });
      })
      .catch(() => {
        if (!cancelled) setResult((previous) => ({ key: requestKey, report: previous.report, error: "No se pudo cargar la analítica. Intenta de nuevo." }));
      });
    return () => { cancelled = true; };
  }, [range, requestKey, onUnauthorized]);
  const reload = useCallback(() => setRefreshNonce((value) => value + 1), []);

  const presets: Array<[string, () => { from: string; to: string }]> = [
    ["7 días", () => ({ from: daysBefore(today, 6), to: today })],
    ["30 días", () => ({ from: daysBefore(today, 29), to: today })],
    ["90 días", () => ({ from: daysBefore(today, 89), to: today })],
    ["Este mes", () => ({ from: `${today.slice(0, 8)}01`, to: today })]
  ];

  const current = report?.summary.current;
  const previous = report?.summary.previous;

  return (
    <>
      <section className="page-heading">
        <div><p className="eyebrow">Quiz de escalamiento</p><h1>Analítica del quiz</h1></div>
        <div className="qa-range">
          <div className="qa-presets" role="group" aria-label="Rango rápido">
            {presets.map(([label, value]) => {
              const next = value();
              const active = next.from === range.from && next.to === range.to;
              return <button key={label} className={`button ${active ? "primary" : "secondary"}`} aria-pressed={active} onClick={() => setRange(next)}>{label}</button>;
            })}
          </div>
          <label>Desde<input type="date" value={range.from} max={range.to} onChange={(event) => event.target.value && setRange({ ...range, from: event.target.value })} /></label>
          <label>Hasta<input type="date" value={range.to} min={range.from} max={today} onChange={(event) => event.target.value && setRange({ ...range, to: event.target.value })} /></label>
          <button className="button secondary" onClick={reload} disabled={loading}>{loading ? "Cargando…" : "Actualizar"}</button>
          <a className="button secondary" href={`/api/v1/admin/analytics/leads.csv?from=${range.from}&to=${range.to}`}><Download size={16} /> CSV</a>
        </div>
      </section>

      {error && <div className="error-summary" role="alert">{error}</div>}
      {report?.dataSince && (
        <p className="qa-note">
          Visitas y pasos del quiz desde el {new Date(report.dataSince).toLocaleDateString("es-MX", { dateStyle: "long" })}; los leads
          anteriores vienen de los envíos guardados. Fechas en hora de Ciudad de México.
        </p>
      )}
      {!report && loading && <p className="qa-note">Cargando…</p>}

      {current && previous && report && (
        <div aria-busy={loading}>
          <section className="qa-kpis" aria-label="Resumen">
            <Kpi icon={<Eye />} label="Visitas" value={current.sessions} previous={previous.sessions} />
            <Kpi icon={<PlayCircle />} label="Iniciaron el quiz" value={current.started} previous={previous.started} rate={pct(current.started, current.sessions)} rateLabel="de las visitas" />
            <Kpi icon={<Target />} label="Llegaron al formulario" value={current.reachedForm} previous={previous.reachedForm} rate={pct(current.reachedForm, current.started)} rateLabel="de quienes iniciaron" />
            <Kpi icon={<Users />} label="Leads" value={current.leads} previous={previous.leads} rate={pct(current.leads, current.reachedForm)} rateLabel="del formulario" />
            <Kpi icon={<Sparkles />} label="Calificados (≥ USD 100k)" value={current.qualified} previous={previous.qualified} rate={pct(current.qualified, current.leads)} rateLabel="de los leads" />
            <Kpi icon={<Eye />} label="Vieron su resultado" value={current.resultViewsCalendar + current.resultViewsCourse} previous={previous.resultViewsCalendar + previous.resultViewsCourse} rate={pct(current.resultViewsCalendar + current.resultViewsCourse, current.leads)} rateLabel="de los leads" />
            <Kpi icon={<CalendarCheck />} label="Clic en agendar" value={current.bookingClicks} previous={previous.bookingClicks} rate={pct(current.bookingClicks, current.qualified)} rateLabel="de los calificados" />
            <Kpi icon={<MousePointerClick />} label="Clic al curso" value={current.courseClicks} previous={previous.courseClicks} rate={pct(current.courseClicks, current.leads - current.qualified)} rateLabel="de los no calificados" />
          </section>

          <Panel eyebrow="Tendencia" title="Visitas y leads por día">
            <DailyChart daily={report.daily} />
          </Panel>

          <div className="qa-two">
            <Panel eyebrow="Resultado del quiz" title="Calificados → agendar">
              <Steps rows={[
                ["Leads calificados", current.qualified],
                ["Vieron su resultado (agenda)", current.resultViewsCalendar],
                ["Clic en agendar", current.bookingClicks]
              ]} />
            </Panel>
            <Panel eyebrow="Resultado del quiz" title="No calificados → curso">
              <Steps rows={[
                ["Leads no calificados", current.leads - current.qualified],
                ["Vieron su resultado (curso)", current.resultViewsCourse],
                ["Clic al curso", current.courseClicks]
              ]} />
            </Panel>
          </div>

          <Panel eyebrow="Embudo" title="Avance y abandono por pregunta" note="Cada persona se cuenta hasta la pregunta más lejana a la que llegó. El ancho es cuánta gente llegó a esa pregunta; la parte roja, quienes se salieron justo ahí.">
            <FunnelView funnel={report.funnel} resumed={report.funnelResumed} />
          </Panel>

          <Panel eyebrow="Origen" title="De dónde llegan y qué logran">
            <Breakdowns breakdowns={report.breakdowns} />
          </Panel>

          <div className="qa-three">
            <Panel eyebrow="Perfil" title="Nivel"><ProfileTable rows={report.profile.level} label={levelLabel} /></Panel>
            <Panel eyebrow="Perfil" title="Ingresos anuales"><ProfileTable rows={report.profile.annualRevenue} label={(key) => answerLabel("annualRevenue", key)} /></Panel>
            <Panel eyebrow="Perfil" title="Tamaño del equipo"><ProfileTable rows={report.profile.teamSize} label={(key) => answerLabel("teamSize", key)} /></Panel>
          </div>

          <Panel eyebrow="Respuestas" title="Qué responde cada lead">
            <AnswersTable answers={report.answers} />
          </Panel>

          <div className="qa-two">
            <Panel eyebrow="Formulario" title="Errores de validación" note="Cuántas veces cada campo frenó un envío.">
              <SimpleTable
                head={["Campo", "Motivo", "Veces"]}
                rows={report.formErrors.map((row) => [fieldLabels[row.field] ?? row.field, reasonLabels[row.reason] ?? row.reason, number.format(row.count)])}
                empty="Sin errores en el período."
              />
              {report.submitErrors.length > 0 && (
                <SimpleTable head={["Error al enviar", "Veces"]} rows={report.submitErrors.map((row) => [reasonLabels[row.reason] ?? row.reason, number.format(row.count)])} empty="" />
              )}
            </Panel>
            <Panel eyebrow="Página de resultado" title="Video y botones">
              <SimpleTable
                head={["Acción", "Detalle", "Visitas"]}
                rows={report.resultPage.map((row) => [resultEventLabel(row.event), resultDetailLabel(row.event, row.detail), number.format(row.sessions)])}
                empty="Sin visitas a resultados en el período."
              />
            </Panel>
          </div>

          <Panel eyebrow="Leads" title="Últimos leads (sin datos personales)">
            <SimpleTable
              head={["Fecha", "Nivel", "Ingresos", "Equipo", "Calificado", "Canal", "Campaña", "Anuncio", "Dispositivo", "País", "Tiempo", "Clic en resultado"]}
              rows={report.recentLeads.map((lead) => [
                new Date(lead.acceptedAt).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short", timeZone: report.timeZone }),
                `${levelLabel(lead.level)} (${lead.score})`,
                lead.annualRevenue ? answerLabel("annualRevenue", lead.annualRevenue) : "—",
                lead.teamSize ? answerLabel("teamSize", lead.teamSize) : "—",
                lead.qualified ? "Sí" : "No",
                lead.channel, lead.campaign ?? "—", lead.ad ?? "—", lead.device ?? "—", lead.country ?? "—",
                lead.completionSeconds == null ? "—" : formatSeconds(lead.completionSeconds),
                lead.clicked ? (lead.qualified ? "Agendar" : "Curso") : "—"
              ])}
              empty="Sin leads en el período."
            />
          </Panel>
        </div>
      )}
    </>
  );
}

function resultEventLabel(event: string) {
  return ({
    quiz_result_view: "Vio su resultado", quiz_video_start: "Inició el video", quiz_video_progress: "Avance del video",
    quiz_video_complete: "Terminó el video", quiz_cta_click: "Clic en el botón", quiz_transcript_open: "Abrió la transcripción"
  } as Record<string, string>)[event] ?? event;
}
function resultDetailLabel(event: string, detail: string) {
  if (event === "quiz_cta_click") return detail === "agendar" ? "Agendar llamada" : detail === "crece_tu_negocio" ? "Curso Crece tu negocio" : detail;
  if (detail === "calendar") return "Oferta: agenda";
  if (detail === "course") return "Oferta: curso";
  return detail || "—";
}

function formatSeconds(seconds: number) {
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${seconds % 60} s`;
}

function Kpi({ icon, label, value, previous, rate, rateLabel }: { icon: React.ReactNode; label: string; value: number; previous: number; rate?: string; rateLabel?: string }) {
  const change = previous > 0 ? Math.round(((value - previous) / previous) * 100) : null;
  return (
    <div className="qa-kpi">
      <span className="metric-icon">{icon}</span>
      <div>
        <p>{label}</p>
        <strong>{number.format(value)}</strong>
        {rate && <span className="qa-rate">{rate} {rateLabel}</span>}
        <span className={`qa-change ${change == null ? "" : change >= 0 ? "up" : "down"}`}>
          {change == null ? `antes: ${number.format(previous)}` : `${change >= 0 ? "▲" : "▼"} ${Math.abs(change)}% vs. período anterior`}
        </span>
      </div>
    </div>
  );
}

function Panel({ eyebrow, title, note, children }: { eyebrow: string; title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="qa-panel">
      <div className="section-heading"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div></div>
      {note && <p className="qa-note">{note}</p>}
      {children}
    </section>
  );
}

function Steps({ rows }: { rows: Array<[string, number]> }) {
  const first = rows[0]?.[1] ?? 0;
  return (
    <ol className="qa-steps">
      {rows.map(([label, value], index) => (
        <li key={label}>
          <div className="qa-step-label"><span>{label}</span><strong>{number.format(value)}</strong></div>
          <div className="qa-bar"><span style={{ width: first > 0 ? `${Math.min(100, (value / first) * 100)}%` : "0%" }} /></div>
          {index > 0 && <small>{pct(value, rows[index - 1]?.[1] ?? 0)} del paso anterior</small>}
        </li>
      ))}
    </ol>
  );
}

function DailyChart({ daily }: { daily: Report["daily"] }) {
  const width = 960;
  const height = 220;
  const pad = { top: 16, right: 12, bottom: 28, left: 40 };
  const maxSessions = Math.max(1, ...daily.map((day) => day.sessions));
  const maxLeads = Math.max(1, ...daily.map((day) => day.leads));
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const step = innerWidth / Math.max(1, daily.length);
  const barWidth = Math.max(2, step * 0.6);
  const line = daily.map((day, index) => `${index === 0 ? "M" : "L"}${pad.left + step * index + step / 2},${pad.top + innerHeight - (day.sessions / maxSessions) * innerHeight}`).join(" ");
  const labelEvery = Math.ceil(daily.length / 10);
  return (
    <figure className="qa-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Leads (barras) y visitas (línea) por día">
        {[0, 0.5, 1].map((tick) => (
          <g key={tick}>
            <line x1={pad.left} x2={width - pad.right} y1={pad.top + innerHeight * (1 - tick)} y2={pad.top + innerHeight * (1 - tick)} className="qa-grid" />
            <text x={pad.left - 6} y={pad.top + innerHeight * (1 - tick) + 4} textAnchor="end" className="qa-axis">{Math.round(maxLeads * tick)}</text>
          </g>
        ))}
        {daily.map((day, index) => {
          const x = pad.left + step * index + (step - barWidth) / 2;
          const leadsHeight = (day.leads / maxLeads) * innerHeight;
          const qualifiedHeight = (day.qualified / maxLeads) * innerHeight;
          return (
            <g key={day.day}>
              <title>{`${day.day}: ${day.sessions} visitas, ${day.leads} leads, ${day.qualified} calificados`}</title>
              <rect x={x} y={pad.top + innerHeight - leadsHeight} width={barWidth} height={leadsHeight} className="qa-bar-leads" />
              <rect x={x} y={pad.top + innerHeight - qualifiedHeight} width={barWidth} height={qualifiedHeight} className="qa-bar-qualified" />
              {index % labelEvery === 0 && <text x={x + barWidth / 2} y={height - 8} textAnchor="middle" className="qa-axis">{day.day.slice(5)}</text>}
            </g>
          );
        })}
        <path d={line} className="qa-line" />
      </svg>
      <figcaption className="qa-legend">
        <span><i className="qa-swatch leads" /> Leads</span>
        <span><i className="qa-swatch qualified" /> Calificados</span>
        <span><i className="qa-swatch sessions" /> Visitas (escala propia, máx. {number.format(maxSessions)})</span>
      </figcaption>
    </figure>
  );
}

/** The quiz as a funnel: every visit counts up to the furthest step it reached, and stops (in red) where it left. */
function FunnelView({ funnel, resumed }: { funnel: Report["funnel"]; resumed: number }) {
  if (!funnel.length) return <p className="qa-note">Sin visitas en el período.</p>;
  const byStep = new Map(funnel.filter((row) => row.questionId !== "submitted").map((row) => [row.step, row]));
  const formStep = quizQuestions.length + 1;
  const lastStep = Math.max(formStep, ...byStep.keys());
  const base = byStep.get(1)?.sessions ?? 0;
  const finished = funnel.find((row) => row.questionId === "submitted")?.sessions ?? 0;
  const steps = Array.from({ length: lastStep }, (_, index) => {
    const step = index + 1;
    const row = byStep.get(step);
    const questionId = row?.questionId || (quizQuestions[index]?.id ?? "contact");
    const sessions = row?.sessions ?? 0;
    const exits = row?.exits ?? 0;
    const rate = sessions > 0 ? exits / sessions : 0;
    return {
      step, questionId, sessions, exits, rate, medianSeconds: row?.medianSeconds ?? null, changedAnswers: row?.changedAnswers ?? 0,
      severity: exits === 0 ? "none" : rate >= 0.15 ? "high" : rate >= 0.08 ? "medium" : "low"
    };
  });
  const worst = steps.filter((row) => row.exits > 0).sort((a, b) => b.exits - a.exits || b.rate - a.rate).slice(0, 3);
  const label = (row: { step: number; questionId: string }) => row.questionId === "contact" ? "Formulario de contacto" : `Pregunta ${row.step}`;
  const width = (value: number) => base > 0 && value > 0 ? `${Math.max(2, (value / base) * 100)}%` : "0%";
  return (
    <>
      <div className="qf-summary">
        <div className="qf-total">
          <strong>{number.format(base)}</strong><span>empezaron el quiz</span>
          <strong>{number.format(finished)}</strong><span>enviaron el formulario ({pct(finished, base)})</span>
        </div>
        <div className="qf-worst">
          <p>Dónde se salen</p>
          {worst.length > 0 ? (
            <ol>
              {worst.map((row) => (
                <li key={row.step}>
                  <strong>{label(row)}</strong>{row.questionId !== "contact" ? `: ${questionPrompt(row.questionId)}` : ""}{" "}
                  <span className="qf-lost">{number.format(row.exits)} {row.exits === 1 ? "persona" : "personas"} ({pct(row.exits, row.sessions)} de quienes llegaron)</span>
                </li>
              ))}
            </ol>
          ) : <span className="qf-muted">Nadie se ha salido en el período.</span>}
        </div>
      </div>
      <ol className="qf-funnel">
        {steps.map((row) => (
          <li key={row.step} className={`qf-row ${row.severity}`}>
            <div className="qf-label">
              <span className="qf-number">{row.step}</span>
              <div>
                <strong>{label(row)}</strong>
                {row.questionId !== "contact" && <p>{questionPrompt(row.questionId)}</p>}
              </div>
            </div>
            <div className="qf-shape" title={`${row.sessions} llegaron · ${row.exits} se salieron aquí`}>
              <span className="qf-bar" style={{ width: width(row.sessions), "--exit": `${row.sessions > 0 ? (row.exits / row.sessions) * 50 : 0}%` } as CSSProperties} />
            </div>
            <div className="qf-numbers">
              <span><strong>{number.format(row.sessions)}</strong> llegaron · {pct(row.sessions, base)}</span>
              {row.exits > 0
                ? <span className="qf-lost">{number.format(row.exits)} se salieron aquí ({pct(row.exits, row.sessions)})</span>
                : <span className="qf-muted">{row.sessions > 0 ? "nadie se salió aquí" : "—"}</span>}
              {(row.medianSeconds != null || row.changedAnswers > 0) && (
                <small>{row.medianSeconds != null ? `${row.medianSeconds} s en responder` : ""}{row.medianSeconds != null && row.changedAnswers > 0 ? " · " : ""}{row.changedAnswers > 0 ? `${number.format(row.changedAnswers)} cambiaron respuesta` : ""}</small>
              )}
            </div>
          </li>
        ))}
        <li className="qf-row done">
          <div className="qf-label">
            <span className="qf-number">✓</span>
            <div><strong>Enviaron el formulario</strong><p>Se convirtieron en lead</p></div>
          </div>
          <div className="qf-shape"><span className="qf-bar" style={{ width: width(finished) }} /></div>
          <div className="qf-numbers"><span><strong>{number.format(finished)}</strong> · {pct(finished, base)} de quienes empezaron</span></div>
        </li>
      </ol>
      {resumed > 0 && (
        <p className="qa-note">
          Además, {number.format(resumed)} {resumed === 1 ? "visita retomó" : "visitas retomaron"} un quiz que había quedado a medias en ese navegador;
          no se cuentan en el embudo para no contar dos veces a la misma persona.
        </p>
      )}
      <details className="qf-details">
        <summary>Ver la tabla completa</summary>
        <FunnelTable steps={steps} base={base} />
      </details>
    </>
  );
}

function FunnelTable({ steps, base }: { steps: Array<{ step: number; questionId: string; sessions: number; exits: number; medianSeconds: number | null; changedAnswers: number }>; base: number }) {
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>Paso</th><th>Pregunta</th><th>Llegaron</th><th>Del inicio</th><th>Se salieron aquí</th><th>% de abandono</th><th>Tiempo mediano</th><th>Cambiaron respuesta</th></tr></thead>
        <tbody>
          {steps.map((row) => (
            <tr key={row.step}>
              <td>{row.step}</td>
              <td>{questionPrompt(row.questionId)}</td>
              <td>{number.format(row.sessions)}</td>
              <td><div className="qa-inline-bar"><span style={{ width: base > 0 ? `${(row.sessions / base) * 100}%` : "0%" }} /><em>{pct(row.sessions, base)}</em></div></td>
              <td>{number.format(row.exits)}</td>
              <td className={row.sessions > 0 && row.exits / row.sessions >= 0.15 ? "qa-alert" : ""}>{pct(row.exits, row.sessions)}</td>
              <td>{row.medianSeconds == null ? "—" : `${row.medianSeconds} s`}</td>
              <td>{number.format(row.changedAnswers)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const breakdownTabs: Array<[keyof Report["breakdowns"], string]> = [
  ["channel", "Canal"], ["campaign", "Campaña"], ["ad", "Anuncio (utm_content)"], ["device", "Dispositivo"], ["country", "País (teléfono)"]
];

function Breakdowns({ breakdowns }: { breakdowns: Report["breakdowns"] }) {
  const [tab, setTab] = useState<keyof Report["breakdowns"]>("channel");
  const rows = breakdowns[tab];
  const showSessions = tab !== "country";
  return (
    <>
      <div className="qa-tabs" role="tablist">
        {breakdownTabs.map(([key, label]) => <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>{label}</button>)}
      </div>
      <SimpleTable
        head={[breakdownTabs.find(([key]) => key === tab)?.[1] ?? "", ...(showSessions ? ["Visitas", "Conversión"] : []), "Leads", "Calificados", "Clic en resultado"]}
        rows={rows.map((row) => [
          row.key,
          ...(showSessions ? [number.format(row.sessions ?? 0), pct(row.leads, row.sessions ?? 0)] : []),
          number.format(row.leads), `${number.format(row.qualified)} (${pct(row.qualified, row.leads)})`,
          `${number.format(row.ctaClicks)} (${pct(row.ctaClicks, row.leads)})`
        ])}
        empty="Sin datos en el período."
      />
    </>
  );
}

function ProfileTable({ rows, label }: { rows: Array<LeadMetrics & { key: string }>; label: (key: string) => string }) {
  const total = rows.reduce((sum, row) => sum + row.leads, 0);
  return (
    <SimpleTable
      head={["", "Leads", "Calificados", "Clic"]}
      rows={rows.map((row) => [label(row.key), `${number.format(row.leads)} (${pct(row.leads, total)})`, pct(row.qualified, row.leads), pct(row.ctaClicks, row.leads)])}
      empty="Sin leads."
    />
  );
}

function AnswersTable({ answers }: { answers: Report["answers"] }) {
  const questions = useMemo(() => quizQuestions.map((question) => question.id as string), []);
  const [question, setQuestion] = useState(questions[0] ?? "");
  const rows = answers.filter((row) => row.questionId === question);
  const total = rows.reduce((sum, row) => sum + row.leads, 0);
  return (
    <>
      <label className="qa-select">Pregunta
        <select value={question} onChange={(event) => setQuestion(event.target.value)}>
          {questions.map((id, index) => <option key={id} value={id}>{index + 1}. {questionPrompt(id)}</option>)}
        </select>
      </label>
      <SimpleTable
        head={["Respuesta", "Leads", "Calificados"]}
        rows={rows.map((row) => [
          answerLabel(row.questionId, row.answerId),
          <div key="bar" className="qa-inline-bar"><span style={{ width: total > 0 ? `${(row.leads / total) * 100}%` : "0%" }} /><em>{number.format(row.leads)} · {pct(row.leads, total)}</em></div>,
          `${number.format(row.qualified)} (${pct(row.qualified, row.leads)})`
        ])}
        empty="Sin leads en el período."
      />
    </>
  );
}

function SimpleTable({ head, rows, empty }: { head: string[]; rows: Array<Array<React.ReactNode>>; empty: string }) {
  if (!rows.length && !empty) return null;
  return (
    <div className="table-wrap qa-table">
      <table>
        <thead><tr>{head.map((cell, index) => <th key={index}>{cell}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}
          {!rows.length && <tr><td colSpan={head.length} className="empty">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
